import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { scrollToSection } from '../utils/scrollToSection';
import ContactActions from './ContactActions';
import { shade, withAlpha } from '../utils/color';
import { getWhatsAppLink } from '../data/contactDetails';

const AUTO_OPEN_DELAY = 10000; // ms — auto-opens 10s after the page loads

// Each answer is a short message, optionally followed by a list of
// [label, detail] rows, a closing note, and an action ('contact' shows the
// Send Email / Call Now buttons; 'services' links to the services section).
const QUESTIONS = [
  {
    id: 'contact',
    q: 'How can I contact you?',
    a: 'You can reach our team directly by email or phone.',
    action: 'contact',
  },
  {
    id: 'services',
    q: 'What services do you offer?',
    a: 'From web and mobile apps to CRM, ERP, business automation and AI, we build software around the way your business works.',
    action: 'services',
  },
  {
    id: 'pricing',
    q: 'How much does a project cost?',
    a: 'Every project is quoted individually. We charge in one of three ways:',
    items: [
      ['Fixed price', 'For projects with a clear scope — one agreed quote upfront.'],
      ['Monthly retainer', 'For ongoing development, support and maintenance.'],
      ['Hourly rate', 'For smaller changes, or work where the scope may evolve.'],
    ],
    note: 'Payments are made in stages: a small advance to begin, and the rest as each part is delivered. All prices are in INR, plus GST.',
  },
  {
    id: 'timeline',
    q: 'How long does a project take?',
    a: 'Every project is different, so we plan yours around your goals rather than a fixed template. What you can count on:',
    items: [
      ['A clear plan from day one', 'After a short discovery call, you get a timeline broken into milestones, so you know exactly what to expect.'],
      ['Progress you can see', 'Regular updates and working demos, so you’re never left wondering where things stand.'],
      ['Results sooner', 'We deliver the most valuable features first, so your business starts benefiting early.'],
    ],
    note: 'Tell us what you have in mind, and we’ll show you how quickly we can bring it to life.',
    action: 'contact',
  },
];

// Conversation mark: an outlined speech bubble behind a filled one with three
// "typing" dots. `dotColor` knocks the dots out in the background colour.
function ChatIcon({ size = 26, dotColor }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16.5 7.5V6a3 3 0 00-3-3h-8a3 3 0 00-3 3v5.5a3 3 0 003 3H6v2.8l3.2-2.8"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" opacity=".7"
      />
      <path
        d="M10.5 9.5h8a3 3 0 013 3v5a3 3 0 01-3 3H18v2.5l-3.4-2.5h-4.1a3 3 0 01-3-3v-5a3 3 0 013-3z"
        fill="currentColor"
      />
      <circle cx="11.5" cy="15" r="1.1" fill={dotColor} />
      <circle cx="14.5" cy="15" r="1.1" fill={dotColor} />
      <circle cx="17.5" cy="15" r="1.1" fill={dotColor} />
    </svg>
  );
}

// Illustrated open hand for the greeting (skin-tone gradient, separate
// fingers with nails, soft palm lines). Fingers are drawn first so the palm
// covers their bases.
function HandIcon({ size = 30 }) {
  const outline = '#c98a62';
  const nail = '#fde6d6';
  const finger = (x, y, w, h, angle, cx, cy) => (
    <g transform={`rotate(${angle} ${cx} ${cy})`}>
      <rect x={x} y={y} width={w} height={h} rx={w / 2} fill="url(#chatbot-skin)" stroke={outline} strokeWidth="1" />
      <rect x={x + w * 0.2} y={y + 1.4} width={w * 0.6} height={w * 0.62} rx={w * 0.28} fill={nail} opacity=".9" />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        {/* One gradient across the whole hand so fingers and palm blend at the joins */}
        <linearGradient id="chatbot-skin" gradientUnits="userSpaceOnUse" x1="12" y1="6" x2="52" y2="60">
          <stop offset="0" stopColor="#fbd9bd" />
          <stop offset="1" stopColor="#e9a57a" />
        </linearGradient>
      </defs>
      {/* thumb */}
      <g transform="rotate(-40 21 47)">
        <rect x="16" y="28" width="9" height="22" rx="4.5" fill="url(#chatbot-skin)" stroke={outline} strokeWidth="1" />
        <rect x="17.8" y="29.6" width="5.4" height="5.4" rx="2.5" fill={nail} opacity=".9" />
      </g>
      {finger(19.5, 9, 7.6, 27, -10, 23.3, 36)}
      {finger(27.6, 5, 7.9, 30, -2, 31.5, 35)}
      {finger(35.6, 8, 7.6, 27, 6, 39.4, 35)}
      {finger(42.8, 15, 6.6, 21.5, 15, 46.1, 36.5)}
      {/* palm */}
      {/* palm: filled shape, outlined only down the sides and round the base,
          so there's no hard line where the fingers join */}
      <path d="M19 33c0-3 2-5 5-5h18.5c3 0 5 2 5 5v11c0 8.3-6.4 14.5-14.2 14.5S19 52.3 19 44z" fill="url(#chatbot-skin)" />
      <path d="M19 34v10c0 8.3 6.4 14.5 14.2 14.5S47.5 52.3 47.5 44V36" fill="none" stroke={outline} strokeWidth="1" strokeLinecap="round" />
      <path d="M25 43.5c4 2 9.5 2.3 14.5.6M27 49.5c3 1.4 7 1.4 10 0" fill="none" stroke={outline} strokeWidth="1" strokeLinecap="round" opacity=".45" />
    </svg>
  );
}

function CloseIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3a9 9 0 00-7.8 13.5L3 21l4.6-1.2A9 9 0 1012 3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path
        d="M9 8.2c.2-.4.5-.4.8-.4h.5c.2 0 .4 0 .5.4l.7 1.7c.1.2 0 .4-.1.6l-.5.6c-.1.1-.1.3 0 .4.6 1.1 1.5 2 2.6 2.6.2.1.3.1.4 0l.6-.6c.2-.2.4-.2.6-.1l1.7.8c.2.1.3.3.3.5v.4c0 .4-.2.8-.6 1-.7.4-1.6.5-2.4.2a8.4 8.4 0 01-4.9-4.9c-.3-.8-.2-1.7.2-2.4z"
        fill="currentColor"
      />
    </svg>
  );
}

const WHATSAPP_GREEN = '#25D366';

// Custom question → opens WhatsApp addressed to us with the message pre-filled.
// (A website can't send WhatsApp messages on its own; the visitor taps send.)
function CustomQuery({ s, colors }) {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState(false);
  const [opened, setOpened] = useState(false);

  const send = () => {
    if (!query.trim()) { setError(true); return; }
    const message = [
      'Hi Deck For Tech, I have a question:',
      '',
      query.trim(),
      ...(name.trim() ? ['', `— ${name.trim()}`] : []),
    ].join('\n');
    window.open(getWhatsAppLink(message), '_blank', 'noopener,noreferrer');
    setOpened(true);
  };

  return (
    <>
      <p style={s.botMsg}>Ask us anything — your question goes straight to our team on WhatsApp.</p>
      <input
        style={s.input}
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Your name"
      />
      <textarea
        style={{ ...s.input, resize: 'none', borderColor: error ? '#ff3d6b' : colors.border }}
        rows={4}
        maxLength={1000}
        placeholder="Type your question…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setError(false); setOpened(false); }}
        aria-label="Your question"
        aria-invalid={error}
      />
      {error && <p style={s.errorText}>Please type your question first.</p>}
      <button style={{ ...s.primaryBtn, ...s.whatsappBtn }} onClick={send}>
        <WhatsAppIcon /> Send on WhatsApp
      </button>
      {opened && (
        <p style={s.note}>
          WhatsApp has opened with your message ready. Just tap send, and we'll get back to you there.
        </p>
      )}
    </>
  );
}

export default function Chatbot() {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null); // null = question menu
  const [bubbleHover, setBubbleHover] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Auto-open the assistant once, 10 seconds after the page loads.
  useEffect(() => {
    const timer = setTimeout(() => setIsOpen(true), AUTO_OPEN_DELAY);
    return () => clearTimeout(timer);
  }, []);

  const backToMenu = () => setSelected(null);

  // Close the assistant and bring the services section into view (from any page).
  const goToServices = () => {
    setIsOpen(false);
    setSelected(null);
    if (pathname === '/') scrollToSection('services');
    else navigate('/#services');
  };

  const s = {
    // Launcher: accent gradient with a soft highlight and a glow in the same
    // colour; lifts slightly on hover.
    bubble: {
      position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 9999,
      width: '60px', height: '60px', borderRadius: '50%',
      background: `linear-gradient(145deg, ${shade(colors.accentBg, 0.18)}, ${shade(colors.accentBg, -0.22)})`,
      color: colors.accentText,
      border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: bubbleHover
        ? `0 14px 34px ${withAlpha(colors.accentBg, 0.55)}, inset 0 1px 0 rgba(255,255,255,0.3)`
        : `0 10px 26px ${withAlpha(colors.accentBg, 0.4)}, inset 0 1px 0 rgba(255,255,255,0.3)`,
      transform: bubbleHover ? 'translateY(-2px) scale(1.04)' : 'none',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    panel: {
      position: 'fixed', bottom: '5.5rem', right: '2rem', zIndex: 9999,
      width: '340px', maxWidth: 'calc(100vw - 2rem)', maxHeight: '70vh',
      display: 'flex', flexDirection: 'column',
      background: colors.surface, border: `1px solid ${colors.border}`,
      borderRadius: '16px', overflow: 'hidden',
      boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '1rem 1.2rem', borderBottom: `1px solid ${colors.border}`,
    },
    headerIdentity: { display: 'flex', alignItems: 'center', gap: '0.7rem' },
    avatar: {
      width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(145deg, ${shade(colors.accentBg, 0.18)}, ${shade(colors.accentBg, -0.22)})`,
      color: colors.accentText,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    headerTitle: { fontSize: '0.95rem', fontWeight: 700, color: colors.text },
    headerSubtitle: { fontSize: '0.72rem', color: colors.muted, marginTop: '2px' },
    closeBtn: {
      background: 'none', border: 'none', color: colors.muted,
      cursor: 'pointer', display: 'flex', padding: '4px',
    },
    body: { padding: '1.2rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' },
    botMsg: { fontSize: '0.88rem', color: colors.text, lineHeight: 1.6, margin: 0 },
    greeting: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
    waveBadge: {
      width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: withAlpha(colors.accentBg, 0.12), color: colors.accent,
    },
    greetTitle: { fontSize: '0.92rem', fontWeight: 700, color: colors.text, lineHeight: 1.3 },
    greetSub: { fontSize: '0.8rem', color: colors.muted, marginTop: '2px' },
    optionsList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
    optionBtn: {
      textAlign: 'left', background: colors.bg2, border: `1px solid ${colors.border}`,
      color: colors.text, padding: '0.7rem 0.9rem', fontSize: '0.82rem',
      borderRadius: '8px', cursor: 'pointer', transition: 'border-color 0.2s',
    },
    primaryBtn: {
      background: colors.accentBg, color: colors.accentText,
      border: 'none', padding: '0.7rem 1rem', fontSize: '0.82rem', fontWeight: 700,
      borderRadius: '8px', cursor: 'pointer', letterSpacing: '0.5px',
    },
    customBtn: {
      textAlign: 'left', background: 'transparent', border: `1px dashed ${withAlpha(colors.accentBg, 0.6)}`,
      color: colors.accent, padding: '0.7rem 0.9rem', fontSize: '0.82rem', fontWeight: 600,
      borderRadius: '8px', cursor: 'pointer',
    },
    input: {
      background: colors.bg2, border: `1px solid ${colors.border}`,
      color: colors.text, padding: '0.75rem 0.9rem', fontSize: '0.85rem',
      borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box',
      fontFamily: "'Inter', sans-serif",
    },
    errorText: { fontSize: '0.76rem', color: '#ff3d6b', margin: 0 },
    whatsappBtn: {
      background: WHATSAPP_GREEN, color: '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    },
    secondaryBtn: {
      background: 'transparent', color: colors.text, border: `1px solid ${colors.border}`,
      padding: '0.7rem 1rem', fontSize: '0.82rem', fontWeight: 600,
      borderRadius: '8px', cursor: 'pointer',
    },
    answerBox: {
      background: colors.bg2, border: `1px solid ${colors.border}`, borderRadius: '8px',
      padding: '0.9rem', fontSize: '0.85rem', color: colors.muted, lineHeight: 1.6,
      display: 'flex', flexDirection: 'column', gap: '0.7rem',
    },
    answerText: { margin: 0 },
    itemList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.55rem' },
    itemLabel: { color: colors.text, fontWeight: 600 },
    note: { margin: 0, fontSize: '0.8rem' },
  };

  return (
    <>
      <button
        aria-label={isOpen ? 'Close chat assistant' : 'Open chat assistant'}
        style={s.bubble}
        className="chatbot-launcher"
        onClick={() => setIsOpen((open) => !open)}
        onMouseEnter={() => setBubbleHover(true)}
        onMouseLeave={() => setBubbleHover(false)}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon dotColor={shade(colors.accentBg, -0.05)} />}
      </button>

      {isOpen && (
        <div style={s.panel} className="chatbot-panel">
          <div style={s.header}>
            <div style={s.headerIdentity}>
              <div style={s.avatar}><ChatIcon size={20} dotColor={colors.accentBg} /></div>
              <div>
                <div style={s.headerTitle}>Deck For Tech Assistant</div>
                <div style={s.headerSubtitle}>Quick answers to common questions</div>
              </div>
            </div>
            <button style={s.closeBtn} onClick={() => setIsOpen(false)} aria-label="Close chat"><CloseIcon size={16} /></button>
          </div>

          <div style={s.body}>
            {!selected && (
              <>
                <div style={s.greeting}>
                  <span style={s.waveBadge}>
                    <span className="chatbot-wave"><HandIcon /></span>
                  </span>
                  <div>
                    <div style={s.greetTitle}>Hello, welcome to Deck For Tech</div>
                    <div style={s.greetSub}>How can we help you today?</div>
                  </div>
                </div>
                <div style={s.optionsList}>
                  {QUESTIONS.map((question) => (
                    <button
                      key={question.id}
                      style={s.optionBtn}
                      onClick={() => setSelected(question)}
                    >
                      {question.q}
                    </button>
                  ))}
                  <button
                    style={s.customBtn}
                    onClick={() => setSelected({ id: 'custom', q: 'Ask your own question' })}
                  >
                    Ask your own question →
                  </button>
                </div>
              </>
            )}

            {selected?.id === 'custom' && (
              <>
                <CustomQuery s={s} colors={colors} />
                <button style={s.secondaryBtn} onClick={backToMenu}>Back to questions</button>
              </>
            )}

            {selected && selected.id !== 'custom' && (
              <>
                <p style={s.botMsg}>{selected.q}</p>
                <div style={s.answerBox}>
                  <p style={s.answerText}>{selected.a}</p>
                  {selected.items && (
                    <ul style={s.itemList}>
                      {selected.items.map(([label, detail]) => (
                        <li key={label}>
                          <div style={s.itemLabel}>{label}</div>
                          <div>{detail}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {selected.note && <p style={s.note}>{selected.note}</p>}
                </div>
                {selected.action === 'contact' && <ContactActions compact />}
                {selected.action === 'services' && (
                  <button style={s.primaryBtn} onClick={goToServices}>Check out our services →</button>
                )}
                <button style={s.secondaryBtn} onClick={backToMenu}>Ask another question</button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatbot-slide-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .chatbot-panel { animation: chatbot-slide-up 0.25s ease; }
        /* Phones: tuck the launcher into the corner and let the panel use the
           screen width (the desktop 2rem offset + 340px would run off-screen). */
        @media (max-width: 600px) {
          .chatbot-launcher { width: 52px !important; height: 52px !important; bottom: 1rem !important; right: 1rem !important; }
          .chatbot-panel {
            left: 0.75rem !important; right: 0.75rem !important; bottom: 4.75rem !important;
            width: auto !important; max-width: none !important; max-height: 75vh !important;
          }
        }
        /* One gentle wave when the greeting appears */
        @keyframes chatbot-wave {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(14deg); }
          40% { transform: rotate(-8deg); }
          60% { transform: rotate(12deg); }
          80% { transform: rotate(-4deg); }
        }
        .chatbot-wave { display: flex; transform-origin: 55% 90%; animation: chatbot-wave 1.4s ease-in-out 0.3s 1; }
        @media (prefers-reduced-motion: reduce) {
          .chatbot-wave { animation: none; }
        }
      `}</style>
    </>
  );
}
